import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { VerticalPanelDialog } from './VerticalPanelDialog';
import { AuthDialog } from './AuthDialog';
import { Zap, Calendar, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { OptimizedImage } from './OptimizedImage';

interface PanelData {
  id: string;
  position: number;
  image_url: string;
  website_url?: string;
  server_id: string;
  start_date: string;
  end_date: string;
  status: string;
  servers?: {
    name: string;
  };
}

export const VerticalPanel = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [activePanel, setActivePanel] = useState<PanelData | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const authDialogTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    fetchActivePanel();
  }, []);

  const fetchActivePanel = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('sky_spots')
        .select('*')
        .in('status', ['active', 'suspended', 'paid'])
        .gte('end_date', today)
        .eq('position', 1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching panel:', error);
        throw error;
      }
      
      // Si on a un panel et qu'il a un server_id, récupérer les infos du serveur
      let panelWithServer: PanelData | null = null;
      if (data) {
        panelWithServer = data as PanelData;
        
        if (data.server_id) {
          const { data: serverData } = await supabase
            .from('servers')
            .select('name')
            .eq('id', data.server_id)
            .single();
          
          if (serverData) {
            panelWithServer.servers = { name: serverData.name };
          }
        }
      }
      
      setActivePanel(panelWithServer);
    } catch (error) {
      console.error('Error fetching panel:', error);
    }
  };

  const handlePanelClick = () => {
    const today = new Date().toISOString().split('T')[0];
    const panelStartsToday = activePanel?.start_date === today;
    
    if (activePanel && activePanel.website_url && (activePanel.status === 'active' || (activePanel.status === 'paid' && panelStartsToday))) {
      window.open(activePanel.website_url, '_blank');
    } else if (!activePanel) {
      // Slot libre - vérifier si l'utilisateur est connecté
      if (!user) {
        authDialogTriggerRef.current?.click();
        return;
      }
      // Ouvrir le dialogue de réservation
      setIsDialogOpen(true);
    }
  };

  const isSuspended = activePanel?.status === 'suspended';
  const isPaid = activePanel?.status === 'paid';
  const isActive = activePanel?.status === 'active';
  const today = new Date().toISOString().split('T')[0];
  const panelStartsToday = activePanel?.start_date === today;
  const panelStartsFuture = activePanel && activePanel.start_date > today;

  return (
    <div className="w-full max-w-[160px] mx-auto">
      <div
        onClick={handlePanelClick}
        className={`
          relative group w-full aspect-[160/600] rounded-lg border-2 transition-all duration-300 overflow-hidden
          ${activePanel 
            ? isSuspended
              ? 'border-slate-600/50 bg-slate-800 cursor-not-allowed'
              : isPaid
                ? 'border-gaming-accent/50 hover:border-gaming-accent bg-slate-800 shadow-lg shadow-gaming-accent/20 cursor-pointer'
                : 'border-gaming-primary/50 hover:border-gaming-primary bg-slate-800 shadow-lg shadow-gaming-primary/20 animate-[pulse_3s_ease-in-out_infinite] cursor-pointer'
            : 'border-slate-700/50 hover:border-gaming-secondary/70 bg-slate-800 hover:bg-slate-700 cursor-pointer'
          }
        `}
        style={{ minHeight: '600px', width: '160px' }}
      >
        {activePanel ? (
          <>
            {/* Image de la publicité */}
            <OptimizedImage 
              src={activePanel.image_url} 
              alt={activePanel.servers?.name || 'Sky Spot'}
              className={`w-full h-full object-cover ${isSuspended ? 'grayscale blur-sm opacity-50' : ''}`}
              loading="eager"
            />
            
            {/* Overlay avec effet au survol (seulement si actif ou démarre aujourd'hui) */}
            {!isSuspended && (isActive || (isPaid && panelStartsToday)) && (
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-500 ease-out flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out transform scale-95 group-hover:scale-100 flex flex-col items-center gap-2 text-white font-semibold">
                  <ExternalLink className="w-4 h-4" />
                  <span className="text-xs text-center px-2">{t('skySpot.visit')}</span>
                </div>
              </div>
            )}

            {/* Overlay pour les panneaux réservés (payés mais pas encore actifs) */}
            {isPaid && panelStartsFuture && (
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <div className="bg-gaming-accent/90 text-white px-2 py-1 rounded text-xs font-semibold text-center">
                  {t('skySpot.reserved')}<br/>{t('skySpot.startsOn')} {new Date(activePanel.start_date).toLocaleDateString()}
                </div>
              </div>
            )}

            {/* Overlay pour les panneaux suspendus */}
            {isSuspended && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="bg-red-500/80 text-white px-2 py-1 rounded text-xs font-semibold">
                  {t('skySpot.suspended')}
                </div>
              </div>
            )}

            {/* Badge du serveur */}
            {activePanel.servers?.name && activePanel.servers.name !== 'Sky Spot Admin' && (
              <div className={`absolute bottom-2 left-2 right-2 backdrop-blur-sm text-white text-xs px-2 py-1 rounded text-center ${isSuspended ? 'bg-red-700/70' : 'bg-black/70'}`}>
                {activePanel.servers.name}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Slot libre */}
            <div className="flex flex-col items-center justify-center h-full text-center p-4">
              <div className="w-12 h-12 bg-gaming-secondary/20 rounded-full flex items-center justify-center mb-3 group-hover:bg-gaming-secondary/30 transition-colors">
                <Zap className="w-6 h-6 text-gaming-secondary" />
              </div>
              <h3 className="font-bold text-white text-sm mb-1">{t('skySpot.title')}</h3>
              <p className="text-slate-400 text-xs">{t('skySpot.clickToReserve')}</p>
            </div>

            {/* Effet lumineux au survol */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-b from-gaming-secondary/10 via-gaming-primary/10 to-gaming-accent/10 rounded-lg"></div>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gaming-secondary via-gaming-primary to-gaming-accent"></div>
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-gaming-accent via-gaming-primary to-gaming-secondary"></div>
            </div>
          </>
        )}
      </div>

      {/* Bouton de réservation */}
      <div className="mt-4 text-center">
        <button
          onClick={() => {
            // Vérifier si l'utilisateur est connecté
            if (!user) {
              authDialogTriggerRef.current?.click();
              return;
            }
            setIsDialogOpen(true);
          }}
          className="w-full px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-gaming-primary/50 hover:border-gaming-primary rounded-lg text-gaming-primary font-semibold transition-all duration-300 hover:scale-105 text-xs"
        >
          <div className="flex items-center gap-2 justify-center">
            <Calendar className="w-3 h-3" />
            <span>{t('skySpot.bookSpot')}</span>
          </div>
        </button>
        <p className="text-slate-500 text-xs mt-2">
          {t('skySpot.bookingDescription')}
        </p>
      </div>

      {/* Dialog de réservation */}
      <VerticalPanelDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={() => {
          setIsDialogOpen(false);
          fetchActivePanel();
        }}
      />

      {/* Dialog d'authentification (caché, déclenché par ref) */}
      <AuthDialog defaultTab="signin">
        <button ref={authDialogTriggerRef} className="hidden" />
      </AuthDialog>
    </div>
  );
};
